/**
 * Web Push 알림 유틸리티
 */
import { tokenManager } from './api';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

/**
 * Service Worker 등록
 */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) {
    console.log('[Push] Service Worker not supported');
    return null;
  }

  // dev 서버는 sw.js를 서빙하지 않는다(VitePWA가 빌드 시에만 생성 → SPA fallback으로
  // index.html이 내려와 MIME SecurityError). 매 페이지 로드·재연결마다 에러가 찍히고,
  // PDF 생성 시 Puppeteer 콘솔 릴레이로 서버 로그에도 실패처럼 남으므로 dev는 등록 skip.
  if (import.meta.env.DEV) {
    console.log('[Push] dev 환경 — Service Worker 등록 skip (sw.js는 빌드 산출물)');
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register('/sw.js');
    console.log('[Push] Service Worker registered:', registration.scope);
    return registration;
  } catch (error) {
    console.error('[Push] Service Worker registration failed:', error);
    return null;
  }
}

/**
 * 알림 권한 요청
 */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!('Notification' in window)) {
    return 'denied';
  }
  if (Notification.permission !== 'default') {
    return Notification.permission;
  }
  return Notification.requestPermission();
}

/**
 * VAPID 공개키 가져오기
 */
async function fetchVapidPublicKey(): Promise<string | null> {
  try {
    const response = await fetch(`${API_URL}/api/v1/push/vapid-public-key`);
    const data = await response.json();
    return data.data?.vapidPublicKey || null;
  } catch {
    console.error('[Push] Failed to fetch VAPID key');
    return null;
  }
}

/**
 * Base64 URL → Uint8Array
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Push 구독 등록
 *
 * ⚠️ 빙의(impersonation) 탭에서는 등록하지 않는다.
 *
 * 브라우저의 push endpoint는 **탭이 아니라 브라우저 프로필**에 하나다(같은 Service Worker
 * 등록을 공유하므로 `pushManager.getSubscription()`이 관리자 본인 탭과 **같은 endpoint**를
 * 돌려준다). 그 endpoint를 빙의 토큰으로 등록하면 서버가 `@CurrentUser().id` = **빙의 대상**으로
 * 행을 만들고, 빙의를 끝내도(sessionStorage 토큰만 지운다) 그 행이 남아 대상의 알림이 관리자
 * 기기로 계속 발송된다 — 2026-09-08 prod 피드백 #61의 원인.
 *
 * 서버(`push.controller.ts subscribe`)가 같은 조건을 정본 게이트로 갖고 있다. 여기서 먼저 끊는
 * 이유는 ① 불필요한 403 왕복 ② 빙의 탭에서 알림 권한 프롬프트가 뜨는 것까지 막기 위해서다.
 */
export async function subscribeToPush(token: string): Promise<boolean> {
  if (tokenManager.isImpersonating()) {
    console.log('[Push] 빙의 세션 — 구독 등록 skip (관리자 기기로 대상 알림이 새는 것 방지)');
    return false;
  }

  try {
    const registration = await registerServiceWorker();
    if (!registration) return false;

    const permission = await requestNotificationPermission();
    if (permission !== 'granted') {
      console.log('[Push] Notification permission not granted:', permission);
      return false;
    }

    const vapidPublicKey = await fetchVapidPublicKey();
    if (!vapidPublicKey) return false;

    // 기존 구독 확인
    let subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey).buffer as ArrayBuffer,
      });
    }

    // 서버에 구독 등록
    const response = await fetch(`${API_URL}/api/v1/push/subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        endpoint: subscription.endpoint,
        keys: {
          auth: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth')!))),
          p256dh: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh')!))),
        },
      }),
    });

    if (!response.ok) {
      console.error('[Push] Subscribe API failed:', response.status);
      return false;
    }

    console.log('[Push] Successfully subscribed to push notifications');
    return true;
  } catch (error) {
    console.error('[Push] Subscribe failed:', error);
    return false;
  }
}

/**
 * Push 구독 해제
 */
export async function unsubscribeFromPush(token: string): Promise<void> {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      await fetch(`${API_URL}/api/v1/push/unsubscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ endpoint: subscription.endpoint }),
      });
      await subscription.unsubscribe();
    }
  } catch (error) {
    console.error('[Push] Unsubscribe failed:', error);
  }
}
